import React from 'react';
import {useMutation} from '@tanstack/react-query';

import {useExperimentsStore, useTemporalStore} from 'apps/experiments/src/store';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {useExperimentsApi} from 'apps/experiments/src/api/experiments';
import {useTranslation} from 'common/i18n/hooks';

import {useExperimentPageContext} from '../context';
import {PageTrackingEvent, trackExperimentPageAction} from '../tracking';
import {useStagedExperiment} from './staged-experiment';

export function useLaunchExperiment() {
  const {t} = useTranslation();
  const {projectId, workspaceId} = useExperimentPageContext();

  const {stagedExperiment: experiment} = useStagedExperiment();
  const setExperiment = useExperimentsStore((state) => state.setExperiment);
  const setExperimentLoading = useExperimentsStore((state) => state.setExperimentLoading);
  const {pause: pauseHistory, resume: resumeHistory} = useTemporalStore((state) => state);

  const {launchExperiment} = useExperimentsApi({
    projectId,
    workspaceId,
  });

  const launchMutation = useMutation({
    mutationFn: launchExperiment,
    onSuccess: (data) => {
      pauseHistory();
      setExperiment(data);
      resumeHistory();
    },
    onError: () => {
      addToast({
        message: t(`experiments.actions.launch.fail`),
        type: ToastType.ERROR,
        timeout: 6000,
      });
    },
    onSettled: () => {
      setExperimentLoading(false);
    },
  });

  const launch = React.useCallback(() => {
    setExperimentLoading(true);
    launchMutation.mutate({
      id: experiment.id,
    });
    trackExperimentPageAction({
      event: PageTrackingEvent.Launch,
      experiment,
    });
  }, [experiment, launchMutation, setExperimentLoading]);

  return launch;
}
